import React, { useState } from "react";
import {
  Typography,
  Paper,
  useTheme,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  Stack,
} from "@mui/material";
import { useParams } from "react-router-dom";
import {
  useGetAccountCommentsQuery,
  useGetCategoriesQuery,
  useGetMapRiskParametersQuery,
  useGetMapSummaryQuery,
  useGetMapTransactionValuesQuery,
  useGetOriginationTabQuery,
} from "api/api-maps";
import { Form, Formik } from "formik";
import {
  defaultRiskScoring,
  getFinalScore,
  getScoringtableInitialValues,
} from "components/Risk/defaults";
import SupplierBuyerScore from "./SupplierBuyerScore";
import Summary from "components/Map/Summary/MapSummaries/Summary";

import {
  useGetComplianceScoringQuery,
  useGetCreditReportQuery,
  useGetSummariesQuery,
} from "api/api-compliance";
import Layout from "components/Common/Layout";
import { PERMISSIONS } from "permissions";
import { useAppSelector } from "redux/hooks";
import {
  Assessment as AssessmentIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";

import styles from "../CC-MEMO/cc-memo.module.css";

import html2pdf from "html2pdf.js";
import { MapContext } from "pages/maps/map";
import { useGetCreditLimitsQuery } from "api/api-origination";

const tableContainerStyles = {
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
  border: "1px solid rgba(224, 224, 224, 0.8)",
  mb: 3.5,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 8px 28px rgba(0,0,0,0.08)",
  },
  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  "& .MuiTableHead-root": {
    background: "linear-gradient(135deg, #f5f7fa 0%, #eef2f7 100%)",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    fontWeight: 700,
    color: "#001E61",
    fontSize: "0.95rem",
    letterSpacing: "0.3px",
    padding: "1.25rem",
  },
  "& .MuiTableCell-root": {
    fontSize: "0.95rem",
    lineHeight: 1.6,
    borderBottom: "1px solid rgba(224, 224, 224, 0.6)",
    padding: "1.25rem",
    color: "#2c3e50",
  },
  pageBreakInside: "avoid",
  position: "relative",
};

// Utility function to format numbers to 2 decimal places with rounding
function formatTwoDecimals(value: number | string | undefined): string {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    isNaN(Number(value))
  )
    return "--";
  const num = Number(value);
  // Round to 2 decimals, rounding up if the third decimal is 5 or more
  return (Math.round(num * 100) / 100).toFixed(2);
}

const MemoContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      className={`credit-memo-container ${styles.ccMemoContainer}`}
      sx={{
        width: "98%",
        maxWidth: "1600px",
        mx: "auto",
        py: 2,
        px: { xs: 2, sm: 3, md: 4 },
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: "4px solid",
          borderImage: "linear-gradient(135deg, #001E61 0%, #1976d2 100%) 1",
          borderRadius: "16px",
          pointerEvents: "none",
          zIndex: 1,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: "4px solid",
          borderImage: "linear-gradient(135deg, #001E61 0%, #1976d2 100%) 1",
          borderRadius: "16px",
          pointerEvents: "none",
          zIndex: 1,
          boxShadow: "0 0 20px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      {children}
    </Box>
  );
};

const MemoBanner = ({
  title,
  company,
  id,
}: {
  title: string;
  company?: string;
  id?: string;
}) => {
  return (
    <Box className={styles.memoBanner}>
      <Box>
        <Typography variant="h4" className={styles.memoTitle} gutterBottom>
          {title}
        </Typography>
        <Typography variant="body1" className={styles.memoSubtitle}>
          {company || "Company"} {id ? `• ${id}` : ""}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          bgcolor: "rgba(255,255,255,0.15)",
          borderRadius: "12px",
          px: 2.5,
          py: 1.5,
          backdropFilter: "blur(4px)",
          border: "1px solid rgba(255,255,255,0.2)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, letterSpacing: "0.5px" }}
        >
          {new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Typography>
      </Box>
    </Box>
  );
};

// Enhanced SectionCard component
const SectionCard = ({
  title,
  icon,
  children,
  isFirst = false,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  isFirst?: boolean;
}) => {
  return (
    <Box
      className={styles.sectionCard}
      sx={{
        mt: isFirst ? 0 : 2,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Box className={styles.sectionHeader}>
        <Box className={styles.sectionIcon}>{icon}</Box>
        <Typography className={styles.sectionTitle}>{title}</Typography>
      </Box>
      <Box className={styles.sectionContent}>{children}</Box>
    </Box>
  );
};

const SectionHeader = ({ title }: { title: string }) => {
  return (
    <Box className={styles.subSectionHeader}>
      <Typography className={styles.subSectionTitle}>{title}</Typography>
    </Box>
  );
};

const ExecutiveSummaryRowCommon = ({
  label,
  value,
  highlighted = false,
}: {
  label: string;
  value: string | undefined;
  highlighted?: boolean;
}) => {
  // Add extra vertical spacing if both label and value are present
  const rowSx = {
    bgcolor: highlighted ? "rgba(25, 118, 210, 0.05)" : "inherit",
    ...(label && value
      ? { "& > *": { py: 2 } } // Increase vertical padding for both cells
      : {}),
  };
  return (
    <TableRow sx={rowSx}>
      <TableCell sx={{ fontWeight: 600, width: "70%" }}>{label}</TableCell>
      <TableCell>{value || ""}</TableCell>
    </TableRow>
  );
};

// StyledPaper component
const StyledPaper = ({
  children,
  sx = {},
}: {
  children: React.ReactNode;
  sx?: any;
}) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 3 },
      borderRadius: 2,
      width: "98%",
      maxWidth: "1600px",
      mx: "auto",
      my: 2,
      ...sx,
    }}
  >
    {children}
  </Paper>
);

// Main Credit Memo Component
const FactSheetStatus: React.FC = () => {
  const { id = "" } = useParams();
  const theme = useTheme();

  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);

  // Add state for latest financial indicator for supplier and buyer
  const [latestFinancialIndicator, setLatestFinancialIndicator] =
    useState<any>(null);
  const [latestFinancialIndicatorBuyer, setLatestFinancialIndicatorBuyer] =
    useState<any>(null);

  // Utility functions
  const stopEvent = React.useCallback((e: React.SyntheticEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  // Loading and error states
  const loadingComponent = null;
  const errorComponent = null;

  const {
    data: mapData,
    refetch: refetchMapSummary, // This is the refetch function
  } = useGetMapSummaryQuery(id ?? "", { skip: !id });

  // Call this function to fetch the latest data
  const fetchLatestData = async () => {
    try {
      await refetchMapSummary(); // This will fetch fresh data from the server
    } catch (error) {
      console.error("Error fetching latest data:", error);
    }
  };

  // Example: Call fetchLatestData when needed
  React.useEffect(() => {
    if (id) {
      fetchLatestData();
    }
  }, [id]);

  const { data: mapRiskParams, isFetching: isFetchingMapRiskParams } =
    useGetMapRiskParametersQuery(
      {
        map_name: id ?? "",
        account_type: "buyer",
        item_type: "background",
      },
      { skip: !id }
    );
  const { data: mapRiskParamsSupp } = useGetMapRiskParametersQuery(
    {
      map_name: id ?? "",
      account_type: "supplier",
      item_type: "background",
    },
    { skip: !id }
  );

  const { data: summariesData } = useGetSummariesQuery(id ?? "", { skip: !id });

  const { data: financialIndicators } = useGetMapRiskParametersQuery(
    {
      map_name: id ?? "",
      account_type: "supplier",
      item_type: "financial",
    },
    { skip: !id }
  );

  React.useEffect(() => {
    if (financialIndicators && typeof financialIndicators === "object") {
      const keys = Object.keys(financialIndicators);
      const getDateFromKey = (key: string) => {
        const [monthYear] = key.split("-");
        const [month, year] = monthYear.split("/");
        return new Date(Number(year), Number(month) - 1);
      };
      const latestKey = keys
        .map((key) => ({ key, date: getDateFromKey(key) }))
        .sort((a, b) => b.date.getTime() - a.date.getTime())[0]?.key;

      if (latestKey) {
        setLatestFinancialIndicator(financialIndicators[latestKey]);
      } else {
        setLatestFinancialIndicator(null);
      }
    } else {
      setLatestFinancialIndicator(null);
    }
  }, [financialIndicators]);

  const mapName = React.useContext(MapContext);

  const { data: tabData } = useGetOriginationTabQuery(mapName ?? "", {
    skip: mapName === undefined,
  });
  const {
    data: TransactionData,
    isLoading: isLoadingMapRiskParams,
    refetch: refetchValues,
  } = useGetMapTransactionValuesQuery(
    {
      map_name: id ?? "",
    },
    { skip: !id }
  );
  // const id = React.useContext(MapContext);
  const { data: categories } = useGetCategoriesQuery(id ?? "", {
    skip: !Boolean(id),
  });
  // account_type: accountType as "supplier" | "buyer",
  const { data: creditReportValues } = useGetCreditReportQuery(
    { map_name: mapName ?? "", account_type: "buyer" },
    { skip: !mapName }
  );
  const { data: creditReportValuessupp } = useGetCreditReportQuery(
    { map_name: mapName ?? "", account_type: "supplier" },
    { skip: !mapName }
  );

  const typeofcompanybuyer: any =
    creditReportValues?.finalversion?.["Type of Company"]?.name;
  const typeoflegalbuyer: any =
    creditReportValues?.finalversion?.["Company Legal Type"]?.name;
  const typeofcompanysupp: any =
    creditReportValuessupp?.finalversion?.["Type of Company"]?.name;
  const typeoflegalsupp: any =
    creditReportValuessupp?.finalversion?.["Company Legal Type"]?.name;

  const { data: ComplainsscoringBuyer } = useGetComplianceScoringQuery({
    map_name: mapName ?? "",
    account_type: "buyer",
  });
  const { data: ComplainsscoringSupp } = useGetComplianceScoringQuery({
    map_name: mapName ?? "",
    account_type: "supplier",
  });

  const { data: financialIndicatorsBuyer } = useGetMapRiskParametersQuery(
    {
      map_name: id ?? "",
      account_type: "buyer",
      item_type: "financial",
    },
    { skip: !id }
  );

  // Store only the latest financial indicator in state (buyer)
  React.useEffect(() => {
    if (
      financialIndicatorsBuyer &&
      typeof financialIndicatorsBuyer === "object"
    ) {
      const keys = Object.keys(financialIndicatorsBuyer);
      const getDateFromKey = (key: string) => {
        const [monthYear] = key.split("-");
        const [month, year] = monthYear.split("/");
        return new Date(Number(year), Number(month) - 1);
      };
      const latestKey = keys
        .map((key) => ({ key, date: getDateFromKey(key) }))
        .sort((a, b) => b.date.getTime() - a.date.getTime())[0]?.key;

      if (latestKey) {
        setLatestFinancialIndicatorBuyer(financialIndicatorsBuyer[latestKey]);
      } else {
        setLatestFinancialIndicatorBuyer(null);
      }
    } else {
      setLatestFinancialIndicatorBuyer(null);
    }
  }, [financialIndicatorsBuyer]);

  const { data: accountComments } = useGetAccountCommentsQuery(
    { map_name: id ?? "", account_type: "supplier" },
    { skip: !id }
  );

  const { data: accountCommentsBuyer } = useGetAccountCommentsQuery(
    { map_name: id ?? "", account_type: "buyer" },
    { skip: !id }
  );

  // Permissions and approval status
  const USER_PERMISSIONS = useAppSelector(
    (state) => state.appState.user_permission
  );

  const queryParams = "";
  const {
    data: creditLimits,
    isFetching,
    // refetch,
  } = useGetCreditLimitsQuery(
    {
      creditlimitsparams: `?${new URLSearchParams(
        Object.fromEntries(
          Object.entries(queryParams).filter(
            ([_, value]) => value !== undefined
          )
        ) as Record<string, string>
      )}`,
    },
    {
      pollingInterval: 300000,
      refetchOnReconnect: true,
    }
  );

  // Memoize permissions to prevent unnecessary re-renders
  const permissions = React.useMemo(() => {
    // Always enable export button - set isFieldDisabled to false
    const READ_ONLY = USER_PERMISSIONS?.includes(PERMISSIONS.map_view_risk);
    const isFieldDisabled = false;
    return { READ_ONLY, isFieldDisabled };
  }, [USER_PERMISSIONS]);

  // Enhanced export handlers with improved functionality
  const handleExportPDF = React.useCallback(
    (event?: React.MouseEvent) => {
      if (event) {
        stopEvent(event);
      }

      if (id) {
        // Show generating state
        setIsGeneratingDoc(true);

        try {
          // Get the memo content element and cast it properly
          const element = document.querySelector(".credit-memo-container");

          if (!element) {
            throw new Error("Could not find memo content element");
          }

          // Type assertion to HTMLElement
          const memoContent = element as HTMLElement;

          // Simple configuration for html2pdf
          const opt = {
            margin: 10,
            filename: `credit_memo_${id}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: {
              unit: "mm",
              format: "a4",
              orientation: "portrait" as const,
            },
          };

          // Generate PDF
          html2pdf()
            .from(memoContent)
            .set(opt)
            .save()
            .then(() => {
              setTimeout(() => {
                setIsGeneratingDoc(false);
              }, 1000);
            })
            .catch((error: any) => {
              console.error("PDF generation failed:", error);
              alert("Failed to export PDF. Please try again.");
              setIsGeneratingDoc(false);
            });
        } catch (error) {
          console.error("PDF export failed:", error);
          alert("Failed to export PDF. Please try again.");
          setIsGeneratingDoc(false);
        }
      }
    },
    [id, stopEvent]
  );

  let riskRecommendation: {
    credit_limit: string;
    advance_rate: string | null;
    guarantees: string;
    conditions_precedent: string;
    monitoring_schedule: string;
    repayment_history: string;
    stage: string;
    risk_questions: string;
  } = {
    credit_limit: "",
    advance_rate: "",
    guarantees: "",
    conditions_precedent: "",
    monitoring_schedule: "",
    repayment_history: "",
    stage: "",
    risk_questions: "",
  };

  const rawValue = mapData?.risk_recommendation;
  if (typeof rawValue === "string") {
    try {
      riskRecommendation = JSON.parse(rawValue);
    } catch (e) {
      console.error("Invalid JSON for risk_recommendation", e);
    }
  } else if (typeof rawValue === "object" && rawValue !== null) {
    riskRecommendation = rawValue as typeof riskRecommendation;
  }

  // Return early for loading or error states
  if (loadingComponent) return loadingComponent;
  if (errorComponent) return errorComponent;

  return (
    <Layout
      title="Investment Memo"
      textHeadingOptions={{ fontWeight: "bold", level: 4 }}
      headerConfig={{
        syncAction: refetchMapSummary, // Use the refetch function from the query
        left: 10,
        mid: 0,
        right: 2,
      }}
    >
      <MemoContainer>
        <StyledPaper>
          <MemoBanner
            title="Investment Memo"
            company={mapData?.seller_name}
            id={id}
          />

          <SectionCard title="Deal Summary" icon={<AssessmentIcon />}>
            <Box>
              <Summary
                title=" "
                subject="buyer"
                data={summariesData?.deal || ""}
                readonly
              />
              <Typography>Buyer Summary</Typography>
              <Summary
                title=" "
                subject="buyer"
                data={summariesData?.buyer || ""}
                readonly
              />
              <Typography> Supplier Summary</Typography>
              <Summary
                title=" "
                subject="supplier"
                data={summariesData?.supplier || ""}
                readonly
              />
            </Box>
          </SectionCard>

          <SectionCard title="Additional Details" icon={<AssessmentIcon />}>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <ExecutiveSummaryRowCommon
                    label="Origination Type"
                    value={mapData?.ipl_product || ""}
                  />
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <ExecutiveSummaryRowCommon
                    label="Invoice Payment Return Type"
                    value={mapData?.invoice_payment_return_type}
                  />
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <ExecutiveSummaryRowCommon
                    label="Supplier Platform Track Record"
                    value={
                      TransactionData?.["As per Supplier Call"]?.[
                        "No. of direct trading years"
                      ] || ""
                    }
                  />
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <ExecutiveSummaryRowCommon
                    label="Buyer Avg Payment Dealy"
                    value={
                      TransactionData?.["As per Buyer Call"]?.[
                        "No. of days average delay in payment"
                      ] || ""
                    }
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
          <SectionCard title="Operational Features" icon={<AssessmentIcon />}>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <TableRow sx={{ bgcolor: theme.palette.grey[400] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Tab
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Supplier
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Buyer
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Country
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {mapData?.seller_country_iso3 || ""}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {mapData?.buyer_country_iso3 || ""}
                    </TableCell>
                  </TableRow>

                  {mapData?.ipl_product === "Enhanced Factoring" ? (
                    <>
                      {" "}
                      <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Country risk rating
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {categories?.suppliers_assessment?.map(
                            (sup) =>
                              sup?.item === "Country Risk" && (
                                <>
                                  {sup?.current_risk_selection_metadata
                                    ?.current_risk_rating || ""}
                                </>
                              )
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {
                            mapRiskParams?.[
                              "As per Buyer Call / Credit Report"
                            ]?.["Country Risk"]?.name
                          }
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Score
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {categories?.suppliers_assessment?.map(
                            (sup) =>
                              sup?.item === "Country Risk" && (
                                <>
                                  {sup?.current_risk_selection_metadata
                                    ?.current_risk_rating_score || ""}
                                </>
                              )
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {
                            mapRiskParams?.[
                              "As per Buyer Call / Credit Report"
                            ]?.["Country Risk"]?.value
                          }
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {categories?.suppliers_assessment
                            ?.filter((sup) => sup?.item === "Country Risk")
                            .map((sup, index) => {
                              const score =
                                sup?.current_risk_selection_metadata
                                  ?.current_risk_rating_score;

                              return (
                                <span key={sup?.id || index}>
                                  {Number(score) > 4 ? "Pass" : "Fail"}
                                </span>
                              );
                            })}
                        </TableCell>

                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {mapRiskParams?.[
                            "As per Buyer Call / Credit Report"
                          ]?.["Country Risk"]?.value > 4
                            ? "Pass"
                            : "Fail"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Industry</TableCell>
                        <TableCell>
                          {
                            mapRiskParams?.["As per Buyer Call / Credit Report"]
                              ?.Industry?.name
                          }
                        </TableCell>
                        <TableCell>
                          <Stack>
                            <Typography>
                              {mapRiskParams?.[
                                "As per Buyer Call / Credit Report"
                              ]?.Industry?.name ?? "No Industry"}
                            </Typography>

                            <Typography>
                              (
                              {
                                mapRiskParams?.[
                                  "As per Buyer Call / Credit Report"
                                ]?.["Industry Sub-Sector"]
                              }
                              )
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                        <TableCell>
                          {
                            mapRiskParams?.["As per Buyer Call / Credit Report"]
                              ?.Industry?.value
                          }
                        </TableCell>
                        <TableCell>
                          {
                            mapRiskParams?.["As per Buyer Call / Credit Report"]
                              ?.Industry?.value
                          }
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell>
                          {mapRiskParams?.["As per Buyer Call / Credit Report"]
                            ?.Industry?.value > 4
                            ? "Pass"
                            : "Fail"}
                        </TableCell>
                        <TableCell>
                          {mapRiskParams?.["As per Buyer Call / Credit Report"]
                            ?.Industry?.value > 4
                            ? "Pass"
                            : "Fail"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                        <TableCell>
                          {
                            mapRiskParams?.[
                              "As per Buyer Call / Credit Report"
                            ]?.["Industry Code (GICS/SIC)"]
                          }
                        </TableCell>
                        <TableCell>
                          {
                            mapRiskParamsSupp?.[
                              "As per Supplier Call / Credit Report"
                            ]?.["Industry Code (GICS/SIC)"]
                          }
                        </TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <>
                      <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Country risk rating
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {categories?.suppliers_assessment?.map(
                            (sup) =>
                              sup?.item === "Country Risk" && (
                                <>
                                  {sup?.current_risk_selection_metadata
                                    ?.current_risk_rating || ""}
                                </>
                              )
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {categories?.buyers_assessment?.map(
                            (buyer) =>
                              buyer?.item === "Country Risk" && (
                                <>
                                  {buyer?.current_risk_selection_metadata
                                    ?.current_risk_rating || ""}
                                </>
                              )
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Score
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {categories?.suppliers_assessment?.map(
                            (sup) =>
                              sup?.item === "Country Risk" && (
                                <>
                                  {sup?.current_risk_selection_metadata
                                    ?.current_risk_rating_score || ""}
                                </>
                              )
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {categories?.buyers_assessment?.map(
                            (buyer) =>
                              buyer?.item === "Country Risk" && (
                                <>
                                  {buyer?.current_risk_selection_metadata
                                    ?.current_risk_rating_score || ""}
                                </>
                              )
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {categories?.suppliers_assessment
                            ?.filter((sup) => sup?.item === "Country Risk")
                            .map((sup, index) => {
                              const score =
                                sup?.current_risk_selection_metadata
                                  ?.current_risk_rating_score;

                              return (
                                <span key={sup?.id || index}>
                                  {Number(score) > 4 ? "Pass" : "Fail"}
                                </span>
                              );
                            })}
                        </TableCell>

                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {categories?.buyers_assessment
                            ?.filter((buyer) => buyer?.item === "Country Risk")
                            .map((buyer, index) => {
                              const score =
                                buyer?.current_risk_selection_metadata
                                  ?.current_risk_rating_score;

                              return (
                                <span key={buyer?.id || index}>
                                  {Number(score) > 4 ? "Pass" : "Fail"}
                                </span>
                              );
                            })}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Industry</TableCell>
                        <TableCell>
                          {categories?.buyers_assessment?.map(
                            (buyer) =>
                              buyer?.item === "Industry" && (
                                <>
                                  {buyer?.current_risk_selection_metadata
                                    ?.current_risk_rating || ""}
                                </>
                              )
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack>
                            {categories?.buyers_assessment?.map(
                              (buyer) =>
                                buyer?.item === "Industry" && (
                                  <>
                                    {buyer?.current_risk_selection_metadata
                                      ?.current_risk_rating || ""}
                                  </>
                                )
                            )}
                            <Typography>
                              (
                              {
                                mapRiskParams?.[
                                  "As per Buyer Call / Credit Report"
                                ]?.["Industry Sub-Sector"]
                              }
                              )
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                        <TableCell>
                          {categories?.buyers_assessment?.map(
                            (buyer) =>
                              buyer?.item === "Industry" && (
                                <>
                                  {buyer?.current_risk_selection_metadata
                                    ?.current_risk_rating_score || "0"}
                                </>
                              )
                          )}
                        </TableCell>
                        <TableCell>
                          {categories?.buyers_assessment?.map(
                            (buyer) =>
                              buyer?.item === "Industry" && (
                                <>
                                  {buyer?.current_risk_selection_metadata
                                    ?.current_risk_rating_score || "0"}
                                </>
                              )
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell>
                          {categories?.buyers_assessment
                            ?.filter((buyer) => buyer?.item === "Industry")
                            ?.map((buyer, index) => (
                              <React.Fragment
                                key={buyer?.id || `buyer-${index}`}
                              >
                                {Number(
                                  buyer?.current_risk_selection_metadata
                                    ?.current_risk_rating_score
                                ) > 4
                                  ? "Pass"
                                  : "Fail"}
                              </React.Fragment>
                            ))}
                        </TableCell>
                        <TableCell>
                          {categories?.buyers_assessment
                            ?.filter((buyer) => buyer?.item === "Industry")
                            ?.map((buyer, index) => (
                              <React.Fragment
                                key={buyer?.id || `buyer-${index}`}
                              >
                                {Number(
                                  buyer?.current_risk_selection_metadata
                                    ?.current_risk_rating_score
                                ) > 4
                                  ? "Pass"
                                  : "Fail"}
                              </React.Fragment>
                            ))}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                        <TableCell>
                          {
                            mapRiskParams?.[
                              "As per Buyer Call / Credit Report"
                            ]?.["Industry Code (GICS/SIC)"]
                          }
                        </TableCell>
                        <TableCell>
                          {
                            mapRiskParamsSupp?.[
                              "As per Supplier Call / Credit Report"
                            ]?.["Industry Code (GICS/SIC)"]
                          }
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <SectionHeader title="Supplier" />
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Insurance Coverage
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {"No Data"}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Insurer Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {mapData?.credit_insurer || "--"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <SectionHeader title="Buyer" />
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Collateral/Security
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {riskRecommendation?.guarantees !== "" ? "Yes" : "No"}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Collateral Type
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {riskRecommendation?.guarantees || "--"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
          <SectionCard
            title="Operational Risk Assessment"
            icon={<AssessmentIcon />}
          >
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <TableRow sx={{ bgcolor: theme.palette.grey[400] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Tab
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Supplier
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Buyer
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Turnover Limit
                    </TableCell>
                    <TableCell>
                      {categories?.suppliers_assessment?.find(
                        (supl) => supl?.item === "Turnover"
                      )?.current_risk_selection_metadata?.current_risk_rating ||
                        ""}
                    </TableCell>
                    <TableCell>
                      {mapData?.ipl_product === "Enhanced Factoring" ? (
                        <>
                          {" "}
                          {
                            mapRiskParams?.["As per Buyer Call / Credit Report"]
                              ?.Turnover?.name
                          }
                        </>
                      ) : (
                        <>
                          {categories?.buyers_assessment?.find(
                            (buyer) => buyer?.item === "Turnover"
                          )?.current_risk_selection_metadata
                            ?.current_risk_rating || ""}
                        </>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell>
                      {(() => {
                        const turnoverSupplier =
                          categories?.suppliers_assessment?.find(
                            (supl) => supl?.item === "Turnover"
                          );
                        const score =
                          turnoverSupplier?.current_risk_selection_metadata
                            ?.current_risk_rating_score;

                        return typeof score === "number" && score > 4
                          ? "Pass"
                          : "Fail";
                      })()}
                    </TableCell>

                    <TableCell>
                      {mapData?.ipl_product === "Enhanced Factoring" ? (
                        <>
                          {mapRiskParams?.["As per Buyer Call / Credit Report"]
                            ?.Turnover?.name ?? ""}{" "}
                          {mapRiskParams?.["As per Buyer Call / Credit Report"]
                            ?.Turnover?.value &&
                          mapRiskParams?.["As per Buyer Call / Credit Report"]
                            ?.Turnover?.value > 4
                            ? "Pass"
                            : "Fail"}
                        </>
                      ) : (
                        <>
                          {(() => {
                            const turnoverBuyer =
                              categories?.buyers_assessment?.find(
                                (buyer) => buyer?.item === "Turnover"
                              );
                            const score =
                              turnoverBuyer?.current_risk_selection_metadata
                                ?.current_risk_rating_score;

                            return typeof score === "number" && score > 4
                              ? "Pass"
                              : "Fail";
                          })()}
                        </>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Operational Experience
                    </TableCell>
                    <TableCell>
                      {new Date().getFullYear() -
                        mapRiskParamsSupp?.[
                          "As per Supplier Call / Credit Report"
                        ]?.["Initial operation commenced in"] >
                      3
                        ? "Pass"
                        : "Fail"}
                    </TableCell>
                    <TableCell>
                      {new Date().getFullYear() -
                        mapRiskParams?.["As per Buyer Call / Credit Report"]?.[
                          "Initial operation commenced in"
                        ] >
                      3
                        ? "Pass"
                        : "Fail"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Shareholding</TableCell>
                    <TableCell>
                      {
                        TransactionData?.["As per Supplier Call"]?.[
                          "supplier_buyer_common_shareholding"
                        ]
                      }
                    </TableCell>
                    <TableCell>
                      {
                        TransactionData?.["As per Buyer Call"]?.[
                          "supplier_buyer_common_shareholding"
                        ]
                      }
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <ExecutiveSummaryRowCommon
                    label="Supplier-buyer trade history"
                    value={
                      TransactionData?.["As per Supplier Call"]?.[
                        "No. of direct trading years"
                      ] > 4
                        ? "Pass"
                        : "Fail"
                    }
                  />
                  <ExecutiveSummaryRowCommon
                    label="Supplier-buyer 
receivables ageing"
                    value={
                      TransactionData?.["As per Supplier Call"]?.[
                        "Payment Terms"
                      ] < 120
                        ? "Pass"
                        : "Fail"
                    }
                  />
                  <ExecutiveSummaryRowCommon
                    label="Supplier-buyer claims 
& rebates"
                    value={
                      TransactionData?.["As per Supplier Call"]?.[
                        "% of Credit Notes / Discounts per year"
                      ] > 4
                        ? "Pass"
                        : "Fail"
                    }
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          <SectionCard title="Credit Risk Assessment" icon={<AssessmentIcon />}>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <TableRow sx={{ bgcolor: theme.palette.grey[400] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Tab
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Supplier
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Buyer
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Financials Available
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {latestFinancialIndicator?.["Debt/Equity"] !== ""
                        ? "Yes"
                        : "No"}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {latestFinancialIndicatorBuyer?.["Debt/Equity"] !== ""
                        ? "Yes"
                        : "No"}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Inventory Turnover
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {formatTwoDecimals(
                        latestFinancialIndicator?.["Revenue"] /
                          latestFinancialIndicator?.["Inventory"]
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {formatTwoDecimals(
                        latestFinancialIndicatorBuyer?.["Revenue"] /
                          latestFinancialIndicatorBuyer?.["Inventory"]
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Receivables Turnover
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {formatTwoDecimals(
                        latestFinancialIndicator?.["Revenue"] /
                          latestFinancialIndicator?.["Trade Receivables"]
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {formatTwoDecimals(
                        latestFinancialIndicatorBuyer?.["Revenue"] /
                          latestFinancialIndicatorBuyer?.["Trade Receivables"]
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Avg collection Period financials
                    </TableCell>
                    <TableCell>
                      {formatTwoDecimals(
                        latestFinancialIndicator?.["DOR"]
                          ? latestFinancialIndicator?.["DOR"]
                          : "--"
                      )}
                    </TableCell>
                    <TableCell>
                      {formatTwoDecimals(
                        latestFinancialIndicatorBuyer?.["DOR"]
                          ? latestFinancialIndicatorBuyer?.["DOR"]
                          : "--"
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Profitability Margin
                    </TableCell>
                    <TableCell>
                      {formatTwoDecimals(
                        latestFinancialIndicator?.["Net Profit"]
                          ? latestFinancialIndicator?.["Net Profit"]
                          : "--"
                      )}
                    </TableCell>
                    <TableCell>
                      {formatTwoDecimals(
                        latestFinancialIndicatorBuyer?.["Net Profit"]
                          ? latestFinancialIndicatorBuyer?.["Net Profit"]
                          : "--"
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Debt to Equity
                    </TableCell>
                    <TableCell>
                      {formatTwoDecimals(
                        latestFinancialIndicator?.["Debt/Equity"]
                          ? latestFinancialIndicator?.["Debt/Equity"]
                          : "--"
                      )}
                    </TableCell>
                    <TableCell>
                      {formatTwoDecimals(
                        latestFinancialIndicatorBuyer?.["Debt/Equity"]
                          ? latestFinancialIndicatorBuyer?.["Debt/Equity"]
                          : "--"
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Current ratio
                    </TableCell>
                    <TableCell>
                      {formatTwoDecimals(
                        latestFinancialIndicator?.["Current Ratio"]
                          ? latestFinancialIndicator?.["Current Ratio"]
                          : "--"
                      )}
                    </TableCell>
                    <TableCell>
                      {formatTwoDecimals(
                        latestFinancialIndicatorBuyer?.["Current Ratio"]
                          ? latestFinancialIndicatorBuyer?.["Current Ratio"]
                          : "--"
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Quick ratio</TableCell>
                    <TableCell>
                      {formatTwoDecimals(
                        latestFinancialIndicator?.["Quick Ratio"]
                          ? latestFinancialIndicator?.["Quick Ratio"]
                          : "--"
                      )}
                    </TableCell>
                    <TableCell>
                      {formatTwoDecimals(
                        latestFinancialIndicatorBuyer?.["Quick Ratio"]
                          ? latestFinancialIndicatorBuyer?.["Quick Ratio"]
                          : "--"
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          <SectionCard title="Risk Ratings" icon={<AssessmentIcon />}>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <TableRow sx={{ bgcolor: theme.palette.grey[400] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Tab
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Incomlend
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Compliance
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Coface
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Supplier
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      <Formik
                        enableReinitialize
                        initialValues={{
                          ...defaultRiskScoring,
                          ...getScoringtableInitialValues(categories),
                        }}
                        onSubmit={() => {}}
                      >
                        {(formikProps) => {
                          const {
                            buyer = {},
                            supplier = {},
                            tradingRelationship = {},
                            adjustment = {},
                            buyers_assessment_weight,
                            suppliers_assessment_weight,
                            trading_relationship_weight,
                          } = formikProps?.values;

                          const weightages = {
                            buyers_assessment_weight,
                            suppliers_assessment_weight,
                            trading_relationship_weight,
                          };

                          getFinalScore({
                            buyer,
                            supplier,
                            tradingRelationship,
                            adjustment,
                            weightages,
                          });

                          return (
                            <Form>
                              <SupplierBuyerScore title="" party="supplier" />
                            </Form>
                          );
                        }}
                      </Formik>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {ComplainsscoringSupp?.["aggregate_risk"]?.["label"]}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      ---
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Buyer
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      <Formik
                        enableReinitialize
                        initialValues={{
                          ...defaultRiskScoring,
                          ...getScoringtableInitialValues(categories),
                        }}
                        onSubmit={() => {}}
                      >
                        {(formikProps) => {
                          const {
                            buyer = {},
                            supplier = {},
                            tradingRelationship = {},
                            adjustment = {},
                            buyers_assessment_weight,
                            suppliers_assessment_weight,
                            trading_relationship_weight,
                          } = formikProps?.values;

                          const weightages = {
                            buyers_assessment_weight,
                            suppliers_assessment_weight,
                            trading_relationship_weight,
                          };

                          getFinalScore({
                            buyer,
                            supplier,
                            tradingRelationship,
                            adjustment,
                            weightages,
                          });

                          return (
                            <Form>
                              <SupplierBuyerScore title="" party="buyer" />
                            </Form>
                          );
                        }}
                      </Formik>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {ComplainsscoringBuyer?.["aggregate_risk"]?.["label"]}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    ></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          <SectionCard title=" Risk Assessment" icon={<AssessmentIcon />}>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <TableRow sx={{ bgcolor: theme.palette.grey[400] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Tab
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Type of Company
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Company Legal Type
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Supplier
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {typeofcompanysupp?.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {typeoflegalsupp?.name}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Buyer
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {typeofcompanybuyer?.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {typeoflegalbuyer?.name}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          <SectionCard
            title="Further Comments by Risk Team"
            icon={<AssessmentIcon />}
          >
            <Box sx={{ my: 2 }}>
              <Summary
                title=" "
                subject="buyer"
                data={mapData?.score_card_comments || ""}
                readonly
              />
            </Box>
          </SectionCard>
        </StyledPaper>
      </MemoContainer>
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          display: "flex",
          gap: 2,
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleExportPDF}
          startIcon={<PdfIcon />}
          disabled={isGeneratingDoc}
          sx={{
            borderRadius: "20px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
            padding: "6px 16px",
            fontSize: "0.875rem",
            fontWeight: 500,
            textTransform: "none",
            letterSpacing: "0.2px",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            "&:hover": {
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.16)",
              transform: "translateY(-1px)",
            },
          }}
        >
          {isGeneratingDoc ? "Generating..." : "Download PDF"}
        </Button>
      </Box>
    </Layout>
  );
};

export default FactSheetStatus;
